/**
 * Or's first viewport: hero, a bordered card holding the brief box, and a
 * pale aside. Same props and the same rules as `LandingStep` — §1's ban on
 * a registration form and on clickable examples applies to both designs.
 */

import React, { useRef } from 'react';
import CountedTextarea from '../CountedTextarea';
import { copyFor } from '../audienceCopy';

export default function MockLanding({ audience, brief, onBriefChange, onSubmit, error, submitting }) {
  const copy = copyFor(audience);
  const fieldRef = useRef(null);

  const submit = () => {
    if (submitting) return;
    if (!String(brief || '').trim()) {
      onSubmit();
      fieldRef.current?.focus();
      return;
    }
    onSubmit();
  };

  return (
    // The hero lives INSIDE the left column, as in the mockup — that is
    // what makes the headline wrap rather than span the whole rail.
    //
    // Two rows, not one: the hero spans the left column alone, and
    // the aside sits on the SECOND row so it centres against the brief card
    // rather than against the hero-plus-card stack, where it floated level
    // with the headline and read as unattached to anything.
    <div className="mk-split mk-split--landing">
      <div className="mk-landing-hero">
        <p className="mk-eyebrow">Alchemiq</p>
        <h1 className="mk-h1">{copy.headline}</h1>
        <p className="mk-lede">{copy.support}</p>
      </div>

      <div className="mk-landing-form">
        <div className="mk-card">
            <label className="mk-label" htmlFor="mk-brief">{copy.briefLabel}</label>
            <p className="mk-help">{copy.briefHelper}</p>
            <CountedTextarea
              id="mk-brief"
              ref={fieldRef}
              wrapClassName="mk-textarea-wrap"
              className={`mk-textarea ${error ? 'mk-textarea--error' : ''}`}
              counterClassName="mk-counter"
              value={brief}
              onChange={onBriefChange}
              aria-describedby="mk-brief-examples"
            >
              {/* §1 — illustrative, not clickable, and gone the moment they type. */}
              {!brief && (
                <div className="mk-placeholder" id="mk-brief-examples" aria-hidden="true">
                  {copy.briefExamples.map((example) => <p key={example}>{example}</p>)}
                </div>
              )}
            </CountedTextarea>
            {error && <div className="mk-error">{error}</div>}
            <div className="mk-actions">
              <button type="button" className="mk-btn mk-btn--primary" onClick={submit} disabled={submitting}>
                {copy.primaryAction}
              </button>
            </div>
        </div>
      </div>

      <aside className="mk-aside">
        <h2>{copy.asideTitle}</h2>
        <p className="mk-body">{copy.asideBody}</p>
      </aside>
    </div>
  );
}
